export interface NavigationOptions {
  notify?: boolean;
  replace?: boolean;
}

const navigationStateKey = "__chewNavigation";

interface ChewNavigationState {
  index: number;
  furthest: number;
}

export interface NavigationAvailability {
  back: boolean;
  forward: boolean;
}

function historyRecord(): Record<string, unknown> {
  return window.history.state && typeof window.history.state === "object"
    ? window.history.state as Record<string, unknown>
    : {};
}

function readNavigationState(): ChewNavigationState | undefined {
  const value = historyRecord()[navigationStateKey];
  if (!value || typeof value !== "object") return undefined;
  const { index, furthest } = value as Partial<ChewNavigationState>;
  return Number.isInteger(index) && Number.isInteger(furthest) && index! >= 0 && furthest! >= index!
    ? { index: index!, furthest: furthest! }
    : undefined;
}

function replaceNavigationState(value: ChewNavigationState, path?: string): void {
  window.history.replaceState({ ...historyRecord(), [navigationStateKey]: value }, "", path);
}

function ensureNavigationState(): ChewNavigationState {
  const existing = readNavigationState();
  if (existing) return existing;
  const initial = { index: 0, furthest: 0 };
  replaceNavigationState(initial);
  return initial;
}

export function navigationAvailability(): NavigationAvailability {
  const state = ensureNavigationState();
  return { back: state.index > 0, forward: state.index < state.furthest };
}

/** Fired for every history write, including ones that skip the popstate notify. */
const navigationEvent = "chew:navigation";

export function subscribeNavigationAvailability(listener: () => void): () => void {
  window.addEventListener("popstate", listener);
  window.addEventListener(navigationEvent, listener);
  return () => {
    window.removeEventListener("popstate", listener);
    window.removeEventListener(navigationEvent, listener);
  };
}

export function goBack(): void {
  if (navigationAvailability().back) window.history.back();
}

export function goForward(): void {
  if (navigationAvailability().forward) window.history.forward();
}

function isExternalPath(path: string): boolean {
  return /^[a-z][a-z0-9+.-]*:/i.test(path) || path.startsWith("//");
}

export function navigate(path: string, { notify = true, replace = false }: NavigationOptions = {}) {
  // pushState throws on cross-origin URLs; hand those to the browser instead.
  if (isExternalPath(path)) {
    window.location.assign(path);
    return;
  }
  const current = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  if (current === path) return;
  const navigation = ensureNavigationState();
  if (replace) {
    replaceNavigationState(navigation, path);
  } else {
    const next = navigation.index + 1;
    replaceNavigationState({ ...navigation, furthest: next });
    window.history.pushState({ ...historyRecord(), [navigationStateKey]: { index: next, furthest: next } }, "", path);
  }
  // The app's back/forward controls track every history write, including ones
  // that deliberately skip the route-level popstate notify.
  window.dispatchEvent(new Event(navigationEvent));
  if (notify) window.dispatchEvent(new PopStateEvent("popstate"));
}

export function handleLocalNavigation(event: React.MouseEvent<HTMLAnchorElement>, path: string) {
  if (event.button !== 0 || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
  if (isExternalPath(path)) return;
  event.preventDefault();
  navigate(path);
}
